import type { User } from "@eai-toolkit/client";
import { createFilterOptions } from "@mui/material/Autocomplete";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { SHARED_BOOKMARK_CACHE_IDS } from "../../hooks/useResourceBookmarks";
import {
  useGetRoleByIdQuery,
  useLazyGetRoleByFullNameQuery,
} from "../../store/api/rolesApi";
import { useGetMeQuery, useGetUserByIdQuery } from "../../store/api/usersApi";
import { useUsersWithMap } from "../../store/hooks/useUsersWithMap";
import ResourceSelector from "../ResourceSelector/ResourceSelector";
import type {
  ResourceItem,
  ResourceSelectorProps,
} from "../ResourceSelector/ResourceSelector";

export type UserOrRoleItem = ResourceItem & {
  resourceType: "user" | "role";
  mail?: string;
};

// Minimal stand-in for an id we couldn't resolve to a real user/role (unknown
// entry or free-solo text). Rendered only so the not-found error keeps the raw
// text for context — it is never a genuine resolved item, hence the cast.
const stubItem = (id: string): UserOrRoleItem =>
  ({ id }) as unknown as UserOrRoleItem;

const filterUsersByLabelOrMail = createFilterOptions<ResourceItem>({
  stringify: (option) => {
    const user = option as UserOrRoleItem;
    return `${user.fullName ?? ""} ${user.name ?? ""} ${user.mail ?? ""}`;
  },
});

export interface UserRoleSelectorProps extends Pick<
  ResourceSelectorProps<UserOrRoleItem>,
  "placeholder" | "required" | "sx" | "ariaLabel"
> {
  /** The currently selected user or role ID */
  value: string | null;
  /** Callback when the selected user or role changes */
  onChange: (userId: string | null) => void;
  /** If true, only accept users (no role lookup) */
  usersOnly?: boolean;
  /** Label for the input field */
  label?: string;
  /**
   * Restricts the user list (client-filtered) to this candidate set instead
   * of the full user directory. The current user is always shown under YOU
   * regardless — do not include them here.
   */
  overrideUsers?: User[];
}

const UserRoleSelector = React.memo(function UserRoleSelector({
  value,
  onChange,
  usersOnly = false,
  label = "Submitted by",
  overrideUsers,
  placeholder = "Anyone",
  required,
  ariaLabel,
  sx = { width: (theme) => theme.spacing(35) },
}: UserRoleSelectorProps) {
  const { data: me } = useGetMeQuery();
  const {
    usersList: defaultUsersList,
    isLoading: isLoadingUsersList,
    isFetching: isFetchingUsersList,
  } = useUsersWithMap();
  const usersList = overrideUsers ?? defaultUsersList;

  // currentData (not data): RTK Query's `data` field lags behind an arg
  // change, still showing the *previous* id's result until the new query
  // settles — so right after replacing a resolved role with a new (invalid)
  // value, `data` would keep showing the old role and we'd render it as if
  // it were the current selection. `currentData` is undefined until the
  // request for *this* id specifically completes.
  const { currentData: userById, isFetching: isFetchingUserById } =
    useGetUserByIdQuery({ id: value ?? "" }, { skip: !value });
  const { currentData: roleById, isFetching: isFetchingRoleById } =
    useGetRoleByIdQuery({ id: value ?? "" }, { skip: !value || usersOnly });

  // Roles are never listed as options — the user picks from the (client-
  // filtered) user list only. Typing a role's full name is resolved on blur,
  // see handleFreeSoloBlur below.
  const [getRoleByFullName] = useLazyGetRoleByFullNameQuery();

  // Fall back to email when a user has no name/fullName
  const toUserItem = (user: User): UserOrRoleItem => {
    const displayName = user.fullName || user.name || user.mail || undefined;
    return {
      ...user,
      fullName: displayName,
      resourceType: "user",
    };
  };

  // Client-filtered list of users shown in the dropdown. The current user is
  // always shown under YOU via `sandboxItem` below regardless of whether
  // they're in this list.
  const defaultOptions = useMemo(
    (): UserOrRoleItem[] => usersList.map(toUserItem),
    [usersList],
  );

  const sandboxItem = useMemo(
    (): UserOrRoleItem | undefined => (me ? toUserItem(me) : undefined),
    [me],
  );

  const isLoading =
    (Boolean(value) &&
      (isFetchingUserById || (!usersOnly && isFetchingRoleById))) ||
    (!overrideUsers && (isLoadingUsersList || isFetchingUsersList));

  // Last item resolved for the current selection (dropdown pick or free-solo
  // resolution) — shown while the by-id queries refetch, so the input never
  // flashes the raw id.
  const [lastResolvedItem, setLastResolvedItem] =
    useState<UserOrRoleItem | null>(null);
  useEffect(() => {
    if (!value) setLastResolvedItem(null);
  }, [value]);

  const resourceValue = useMemo((): UserOrRoleItem | null => {
    if (!value) return null;
    if (userById) return toUserItem(userById);
    if (!usersOnly && roleById) return { ...roleById, resourceType: "role" };
    if (lastResolvedItem?.id === value) return lastResolvedItem;
    const knownItem =
      defaultOptions.find((option) => option.id === value) ??
      (sandboxItem?.id === value ? sandboxItem : undefined);
    if (knownItem) return knownItem;
    // Still resolving — render nothing (the spinner shows) rather than the raw id.
    if (isLoading) return null;
    // Unknown entry: show the raw text so the not-found error has context.
    return stubItem(value);
  }, [
    value,
    userById,
    roleById,
    usersOnly,
    lastResolvedItem,
    defaultOptions,
    sandboxItem,
    isLoading,
  ]);

  const handleChange = useCallback(
    (item: UserOrRoleItem | null) => {
      setLastResolvedItem(item);
      onChange(item ? item.id : null);
    },
    [onChange],
  );

  const handleFreeSoloBlur = useCallback(
    async (rawValue: string | null) => {
      if (!rawValue) {
        setLastResolvedItem(null);
        onChange(rawValue);
        return;
      }
      const normalized = rawValue.trim().toLowerCase();
      const userByMail = usersList.find(
        (u) => u.mail?.toLowerCase() === normalized,
      );
      if (userByMail) {
        setLastResolvedItem(toUserItem(userByMail));
        onChange(userByMail.id);
        return;
      }
      if (usersOnly) {
        // Unknown entry: show the typed text while it validates.
        setLastResolvedItem(stubItem(rawValue));
        onChange(rawValue);
        return;
      }
      // Roles can also submit jobs, so try resolving the raw text as a role
      // by full name via the backend. If that fails, pass it through as-is.
      try {
        const role = await getRoleByFullName({ fullName: rawValue }).unwrap();
        setLastResolvedItem({ ...role, resourceType: "role" });
        onChange(role.id);
      } catch {
        setLastResolvedItem(stubItem(rawValue));
        onChange(rawValue);
      }
    },
    [usersList, usersOnly, getRoleByFullName, onChange],
  );

  const errorMessage = useMemo(() => {
    if (!value || isLoading) return null;
    if (userById) return null;
    if (!usersOnly && roleById) return null;
    return usersOnly ? "User does not exist" : "User/role does not exist";
  }, [value, isLoading, userById, roleById, usersOnly]);

  return (
    <ResourceSelector<UserOrRoleItem>
      resourceType="user"
      value={resourceValue}
      onChange={handleChange}
      defaultOptions={defaultOptions}
      sandboxId={me?.id}
      sandboxLabel="Me"
      sandboxItem={sandboxItem}
      cacheId={SHARED_BOOKMARK_CACHE_IDS.userOrRole}
      filterOptions={filterUsersByLabelOrMail}
      freeSolo
      onFreeSoloBlur={handleFreeSoloBlur}
      label={label}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      loading={isLoading}
      required={required}
      inputLabelProps={{ shrink: true }}
      error={errorMessage}
      sx={sx}
      noOptionsText={
        usersOnly
          ? "No user found"
          : "No matching user — press Tab or click away to submit as a role"
      }
    />
  );
});

export { UserRoleSelector };
